import { z } from 'zod';
import { googleMapsUrl } from './google-maps-url';

export const MeetingVisibility = z.enum(['CONNECTIONS', 'PRIVATE']);
export type MeetingVisibility = z.infer<typeof MeetingVisibility>;

const localTime = z.string().regex(/^\d{2}:\d{2}$/, 'HH:MM');

/** `YYYY-MM-DD` + `HH:MM` в одну сравнимую строку (обе даты — в одной таймзоне города). */
const localStamp = (date: string, time?: string | undefined) => `${date}T${time ?? '00:00'}`;

// Время приходит локальным для города организатора, а не готовым ISO-инстантом:
// таймзону знает только сервер (City.timezone), клиент же считал бы UTC по
// таймзоне устройства и уводил встречу на смещение, когда автор не в своём
// городе. Тот же контракт у событий — EventCreateInput.startsAtLocalDate/Time.
export const CreateMeetingInput = z
  .object({
    title: z.string().trim().min(1).max(120),
    description: z.string().max(2000).optional(),
    startsAtLocalDate: z.iso.date(),
    /** Отсутствует — встреча «на дату», без времени (hasStartTime=false). */
    startsAtLocalTime: localTime.optional(),
    endsAtLocalDate: z.iso.date().optional(),
    endsAtLocalTime: localTime.optional(),
    placeId: z.uuid().optional(),
    venueText: z.string().trim().min(1).max(200).optional(),
    externalUrl: googleMapsUrl.optional(),
    visibility: MeetingVisibility,
    inviteUserIds: z.array(z.uuid()).max(20).default([]),
    maxAttendees: z.number().int().min(2).max(500).optional(),
  })
  .refine((v) => !!v.placeId !== !!v.venueText, {
    message: 'Either placeId or venueText must be set, not both',
    path: ['placeId'],
  })
  .refine(
    (v) =>
      !v.endsAtLocalDate ||
      localStamp(v.endsAtLocalDate, v.endsAtLocalTime) > localStamp(v.startsAtLocalDate, v.startsAtLocalTime),
    {
      message: 'endsAt must be after startsAt',
      path: ['endsAtLocalDate'],
    },
  );
export type CreateMeetingInput = z.infer<typeof CreateMeetingInput>;

export const RsvpInput = z.object({
  status: z.enum(['CONFIRMED', 'DECLINED']),
});
export type RsvpInput = z.infer<typeof RsvpInput>;

export const InviteInput = z.object({
  userIds: z.array(z.uuid()).min(1).max(20),
});
export type InviteInput = z.infer<typeof InviteInput>;

export const AcceptViaShareInput = z.object({
  shortCode: z.string().min(1).max(16),
});
export type AcceptViaShareInput = z.infer<typeof AcceptViaShareInput>;

// MeetingDto — карточка встречи для таба «Встречи».
export const MeetingDto = z.object({
  id: z.uuid(),
  title: z.string(),
  startsAt: z.iso.datetime(),
  endsAt: z.iso.datetime().nullable(),
  venueText: z.string(),
  placeId: z.uuid().nullable(),
  organizerUserId: z.uuid().nullable(),
  organizerName: z.string().nullable(),
  organizerPhotoUrl: z.string().nullable(),
  coverImageUrl: z.string().nullable(),
  visibility: z.enum(['CONNECTIONS', 'PRIVATE']),
  attendanceCount: z.number().int(),
  maxAttendees: z.number().int().nullable(),
  myAttendanceStatus: z.enum(['NONE', 'CONFIRMED', 'WAITLISTED', 'INVITED', 'DECLINED']),
  shareShortCode: z.string().nullable(),
});
export type MeetingDto = z.infer<typeof MeetingDto>;

export const MeetingsListResponse = z.object({
  incoming: z.array(MeetingDto),
  outgoing: z.array(MeetingDto),
  confirmed: z.array(MeetingDto),
  past: z.array(MeetingDto),
});
export type MeetingsListResponse = z.infer<typeof MeetingsListResponse>;

/** Стоимость публикации встречи в ленте (≥2 участника). */
export const MEETING_FEED_COST = 10;

/** Списание при создании: встреча сразу попадёт в ленту (организатор + ≥1 приглашённый). */
export function isMeetingFeedChargeableAtCreate(input: {
  visibility: MeetingVisibility;
  matchedInviteeCount: number;
}): boolean {
  if (input.visibility === 'PRIVATE' && input.matchedInviteeCount === 0) return false;
  return input.matchedInviteeCount >= 1;
}

/** Списание при invite/accept: соло-встреча впервые получает второго участника. */
export function isMeetingFeedChargeableOnSoloExpand(input: {
  attendanceCountBefore: number;
  newParticipantsAdded: number;
}): boolean {
  return input.attendanceCountBefore === 1 && input.newParticipantsAdded > 0;
}
